import { ChevronLeftIcon, ChevronRightIcon, type LucideProps } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import * as reactSlick from 'react-slick';
import 'slick-carousel/slick/slick.css';
import { Button } from '../../../components/ui/button.tsx';
import { Card } from '../../../components/ui/card.tsx';
import { CollapsibleContainer } from '../../../components/ui/collapsible-container.tsx';
import { Text } from '../../../components/ui/text.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { cn } from '../../../lib/utils.ts';
import type { RouterOutput } from '../../../server/types/infer-trpc-types.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { useDrawers } from '../../drawers/all-drawers.tsx';

// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- fixing broken types
const Slider = reactSlick.default as unknown as typeof import('react-slick').default;

type Props = {
    data: RouterOutput['platoUser']['getDetailProduct']['getProductDetail'];
};

const settings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 3,
    adaptiveHeight: true,
};

type ItemCardProps = {
    rank: number;
    sourceProductId: string;
    variantId: string;
    productName: string;
};

type ArrowProps = {
    cn: string;
    icon: React.ComponentType<LucideProps>;
    onClick?: () => void;
    currentSlide?: number;
    slideCount?: number;
    slideHeight?: number;
    isVisible: (state: { slideCount: number; currentSlide: number }) => boolean;
};

const ArrowButton = ({
    cn: className,
    icon: Icon,
    onClick,
    isVisible,
    currentSlide = 0,
    slideCount = 0,
}: ArrowProps) => {
    if (!isVisible({ slideCount, currentSlide })) {
        return null;
    }

    return (
        <Button
            variant="icon"
            size="icon"
            icon={Icon}
            className={cn('absolute top-10 z-10 shadow-xl', className)}
            onClick={onClick}
        />
    );
};

const Dots = ({ children }: React.PropsWithChildren) => {
    return <ul className="flex flex-row gap-3 justify-center py-2">{children}</ul>;
};

const DotButton = ({ onClick }: React.ComponentProps<'button'>) => {
    const [isActive, setIsActive] = React.useState(false);
    const ref = React.useRef<HTMLButtonElement>(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    React.useEffect(() => {
        if (!ref.current) {
            return;
        }

        setIsActive(!!(ref.current.parentNode as HTMLUListElement)?.classList.contains('slick-active'));
    });

    return (
        <button
            ref={ref}
            className={cn(
                'bg-neutral-100 border border-neutral-200 hover:bg-neutral-300 hover:border-neutral-300 h-3 w-3 rounded-full',
                {
                    'bg-neutral-300 border-neutral-300': isActive,
                },
            )}
            onClick={onClick}
        ></button>
    );
};

const ItemCard = ({ sourceProductId, rank, variantId, productName }: ItemCardProps) => {
    const { t } = useTranslation();
    const { openProductDetails } = useDrawers();
    const isPointerMovedRef = React.useRef(false);

    const sendEvent = useAnalytics();

    const { ref } = useInView({
        threshold: 0,
        onChange: (inView) => {
            if (inView) {
                sendEvent('related_item_viewed', {
                    source_item_id: sourceProductId,
                    item_id: variantId,
                    rank: rank,
                });
            }
        },
        triggerOnce: true,
    });

    return (
        <div ref={ref} className="w-full h-28 px-1.5">
            <button
                className="w-full h-full"
                onPointerDown={() => {
                    isPointerMovedRef.current = false;
                }}
                onPointerMove={() => {
                    isPointerMovedRef.current = true;
                }}
                onPointerUp={() => {
                    if (isPointerMovedRef.current) {
                        isPointerMovedRef.current = false;
                        return;
                    }

                    sendEvent('related_item_clicked', {
                        source_item_id: sourceProductId,
                        item_id: variantId,
                    });

                    openProductDetails(variantId);
                }}
            >
                <Card className="flex h-full flex-col gap-2 justify-between border-neutral-200">
                    <div className="overflow-hidden flex-shring-1">
                        <Text className="line-clamp-3">{productName}</Text>
                    </div>
                    <Text className="shrink-0" color="neutral-500">
                        {t('product_information__id__product_id', {
                            ns: 'product-details',
                            value: variantId,
                        }).toString()}
                    </Text>
                </Card>
            </button>
        </div>
    );
};

export const InventoryDetailsRecommendations = ({ data: data }: Props) => {
    const { t } = useLocale();
    const sendEvent = useAnalytics();

    if (data.recommendations.length === 0) {
        return null;
    }

    return (
        <CollapsibleContainer
            defaultOpen
            title={t('recommendations', { ns: 'product-details' })}
            tooltipText={t('recommendations__tooltip', { ns: 'product-details' })}
        >
            <div className="pb-5">
                <Slider
                    className="-mx-1.5"
                    prevArrow={
                        <ArrowButton
                            cn="-left-2"
                            icon={ChevronLeftIcon}
                            isVisible={({ currentSlide }) => !!currentSlide}
                        />
                    }
                    nextArrow={
                        <ArrowButton
                            cn="-right-2"
                            icon={ChevronRightIcon}
                            isVisible={({ currentSlide, slideCount }) =>
                                currentSlide + settings.slidesToShow < slideCount
                            }
                        />
                    }
                    afterChange={(currentSlide) => {
                        const loadedItems = data.recommendations
                            .slice(currentSlide, currentSlide + settings.slidesToShow)
                            .map((item) => item.product_variant_id);

                        sendEvent('related_item_carousel_scrolled', {
                            source_item_id: data.product_variant_id,
                            loaded_items: loadedItems,
                        });
                    }}
                    appendDots={(dots) => <Dots>{dots}</Dots>}
                    customPaging={() => <DotButton />}
                    {...settings}
                >
                    {
                        data.recommendations.map(({ product_variant_id, product_variant_name }, index) => (
                            <ItemCard
                                key={product_variant_id}
                                productName={product_variant_name}
                                variantId={product_variant_id}
                                sourceProductId={data.product_variant_id}
                                rank={index + 1}
                            />
                        )) as unknown as React.ReactNode
                    }
                </Slider>
            </div>
        </CollapsibleContainer>
    );
};
